import { useState } from 'react';
import type { Prospect } from '../types';

const PROBABILITY_LABELS: Record<number, string> = {
  10: 'Initial Lead (10%)',
  35: 'Discovery/Proposal (35%)',
  60: 'Verbal Buy-In (60%)',
  90: 'Legal/Contract Review (90%)',
};

const GOLD = '#D4AF37';
const LEATHER = '#3D2B1F';

interface ProspectPipelineProps {
  partnerId: number;
  prospects: Prospect[];
  onRefresh: () => void;
}

function weightedForecast(prospects: Prospect[]): number {
  return prospects.reduce(
    (sum, p) => sum + (p.estimated_value * p.probability_score) / 100,
    0
  );
}

export default function ProspectPipeline({ partnerId, prospects, onRefresh }: ProspectPipelineProps) {
  const [name, setName] = useState('');
  const [estimatedValue, setEstimatedValue] = useState<number | ''>('');
  const [probabilityScore, setProbabilityScore] = useState<10 | 35 | 60 | 90>(35);
  const [submitting, setSubmitting] = useState(false);

  const forecast = weightedForecast(prospects);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || estimatedValue === '' || Number(estimatedValue) < 0) return;
    setSubmitting(true);
    try {
      const { createProspect } = await import('../api');
      await createProspect(String(partnerId), {
        name: name.trim(),
        estimated_value: Number(estimatedValue),
        probability_score: probabilityScore,
      });
      setName('');
      setEstimatedValue('');
      setProbabilityScore(35);
      onRefresh();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border border-leather bg-leather/40 p-5">
      <h3 className="text-lg font-semibold text-gold mb-2">Prospect Pipeline</h3>
      <div
        className="mb-4 p-4 rounded-lg border border-gold/50 text-center"
        style={{ backgroundColor: 'rgba(61, 43, 31, 0.6)' }}
      >
        <p className="text-station-muted text-sm">Weighted Forecast</p>
        <p className="text-2xl font-bold text-gold">${forecast.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
        <p className="text-xs text-station-muted mt-1">Sum of (estimated value × probability ÷ 100)</p>
      </div>

      <form onSubmit={handleAdd} className="flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Prospect name"
          className="flex-1 min-w-[120px] rounded-lg border border-leather bg-leather/30 px-3 py-2 text-white placeholder-station-muted focus:ring-2 focus:ring-gold"
        />
        <input
          type="number"
          min={0}
          step={0.01}
          value={estimatedValue === '' ? '' : estimatedValue}
          onChange={(e) => setEstimatedValue(e.target.value === '' ? '' : Number(e.target.value))}
          placeholder="Est. value"
          className="w-28 rounded-lg border border-leather bg-leather/30 px-3 py-2 text-white placeholder-station-muted focus:ring-2 focus:ring-gold"
        />
        <select
          value={probabilityScore}
          onChange={(e) => setProbabilityScore(Number(e.target.value) as 10 | 35 | 60 | 90)}
          className="rounded-lg border border-leather bg-leather/30 px-3 py-2 text-white focus:ring-2 focus:ring-gold"
        >
          {([10, 35, 60, 90] as const).map((p) => (
            <option key={p} value={p}>
              {PROBABILITY_LABELS[p]}
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={submitting || !name.trim()}
          className="px-4 py-2 rounded-lg bg-gold text-charcoal font-medium hover:bg-gold-light disabled:opacity-50"
        >
          {submitting ? 'Adding…' : 'Add'}
        </button>
      </form>

      <ul className="space-y-2">
        {prospects.length === 0 ? (
          <li className="text-station-muted text-sm">No prospects yet.</li>
        ) : (
          prospects.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg bg-leather/20 border border-leather/50"
            >
              <span className="text-white font-medium truncate">{p.name}</span>
              <span className="text-gold shrink-0">${p.estimated_value.toLocaleString()}</span>
              <span className="text-station-muted text-xs shrink-0">{PROBABILITY_LABELS[p.probability_score]}</span>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
